import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { BuildingThreeIcon } from '@repo/ui/icons'
import { useRef, useState } from 'react'

import { API } from '../../../api/apis.config'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import { CentersFilter } from '../../../components/pages/centers/center-filter'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { Center } from '../../../lib/types/models/center/center'
import { CenterFilter } from '../../../lib/types/models/center/center-filter'
import {
  transformCentersFiltersToUrl,
  transformUrlToCenterFilters
} from '../../../lib/utils/filters-mapping/centers'
import { ChangeFrenchLeaderPhoneNumberModel } from '../../ols/center/change-french-leader-phone-number-model'
import { ChangeHostFamilyModel } from '../../ols/center/change-host-family-model'
import { getOlCenterColumns } from '../../ols/center/utils/ol-center-columns'
import { constructOlPersonalSpaceCenterPath } from './utils/functions'

const OlPersonalSpaceCenterList = () => {
  const { t } = useTranslate()
  const { navigateWithEvent } = useNavigator()
  const { user } = useAuth()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const tableRef = useRef<DynamicTableRef<Center>>(null)

  const olPermissions = getOlPermissions({ role: user.role, type: user.ol?.type })

  const emptyColumns = olPermissions['hide-ol-center-columns']
    ? ['costsStatus', 'centerTefls', 'finalCostsStatus']
    : []

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<CenterFilter>({
      initialFilters: {
        year: new Date().getFullYear(),
        ...(user.ol?.id ? { olIds: [user.ol.id] } : {})
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformCentersFiltersToUrl,
      transformUrlToFilter: transformUrlToCenterFilters
    })

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'filters' | 'mobile-number-for-french-leaders' | 'host-family',
    Center
  >(['filters', 'mobile-number-for-french-leaders', 'host-family'])

  //filter for OL centers
  return (
    <div>
      <UserNavBar title="My centres" icon={BuildingThreeIcon} />
      <div className="h-4" />
      <Box>
        <DynamicDataTable<Center, CenterFilter>
          onLoadingChange={setIsTableLoading}
          className="p-0"
          ref={tableRef}
          title={'All centers'}
          deleteMessage={{ name: 'centre', attributeName: 'name' }}
          sorting={{ id: 'startDate', desc: false }}
          columns={getOlCenterColumns(t, [RoleEnum.Ol], false, {
            emptyColumns,
            olType: user.ol?.type,
            onTelephoneClick: (center) => {
              if (!olPermissions['edit-mobile-number-for-french-leaders']) return
              toggleModal('mobile-number-for-french-leaders', center)
            },
            onTeachingHallClick: (center) => toggleModal('host-family', center)
          })}
          allowSearchParam
          permissions={{ delete: false }}
          onRowClick={(center, event) =>
            navigateWithEvent(
              constructOlPersonalSpaceCenterPath(
                Paths.OL_PERSONAL_SPACE_CENTER_PLACEMENTS,
                center.id
              ),
              event
            )
          }
          filters={selectedFilter}
          filtersLength={countFilters(selectedFilter, ['olIds'])}
          endpoint={API.centers.getAll.url}
          onFiltersClick={() => toggleModal('filters')}
        />
      </Box>

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <CentersFilter mode="ol" filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <>
          <ChangeFrenchLeaderPhoneNumberModel
            data={selectedData}
            isOpen={modalState['mobile-number-for-french-leaders']}
            onCancel={() => {
              tableRef.current?.refetch()
              closeModel('mobile-number-for-french-leaders')
            }}
          />

          <ChangeHostFamilyModel
            data={selectedData}
            isOpen={modalState['host-family']}
            onCancel={() => {
              tableRef.current?.refetch()
              closeModel('host-family')
            }}
          />
        </>
      )}
    </div>
  )
}

export default OlPersonalSpaceCenterList
